import type { ChatMessageGroup } from '@provision/chat-utils';
import { useVisibilityChange } from '@uidotdev/usehooks';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNewMessagesBoundaryStore } from 'src/store/new-messages-boundary';
import { useNewMessagesAnchor } from './new-messages-anchor-context';
import { NewMessagesBoundaryContext } from './new-messages-boundary-context';
import { useChatCommonData } from './registry/common-data-context';
import { useMergedTimeline } from './registry/hooks';
import { messageRegistry } from './registry/index';
import { MessageTypeId, TimelineEntry } from './registry/types';

const BRIEF_SWITCH_MS = 1500;
const DISMISS_FADE_MS = 400;
const SETTLE_MS = 1000;

function getTimelineKey(entry: TimelineEntry<unknown>): string | null {
  const registration = messageRegistry[entry.type];
  return registration ? `${entry.type}:${registration.getKey(entry.item)}` : null;
}

export const NewMessagesBoundaryProvider = ({
  children,
  currentUserId,
  bottomInset = 0,
}: {
  children: React.ReactNode;
  currentUserId?: string;
  /** Composer dock height in px — a divider hidden behind it must not count as "seen". */
  bottomInset?: number;
}): React.JSX.Element => {
  const entries = useMergedTimeline();
  const conversationId = useChatCommonData((s) => s.conversationId);
  const chatRef = useChatCommonData((s) => s.chatRef);
  const isVisible = useVisibilityChange();

  const { anchor, anchorRef, setAnchor } = useNewMessagesAnchor();

  const [isDismissing, setIsDismissing] = useState(false);
  const [dividerEl, setDividerEl] = useState<HTMLElement | null>(null);
  // null = not yet observed by the IntersectionObserver (neither confirmed in nor out
  // of view). Only an explicit true/false from the observer counts as a real signal.
  const [dividerInView, setDividerInView] = useState<boolean | null>(null);
  const [dividerOutOfViewDirection, setDividerOutOfViewDirection] = useState<'up' | 'down'>(
    'down',
  );
  const [hasUserIntent, setHasUserIntent] = useState(false);

  const latestTimestampRef = useRef<string | null>(null);
  const hiddenSnapshotRef = useRef<{ anchor: string | null; hiddenAt: number } | null>(null);
  const settleDeadlineRef = useRef<number | null>(null);
  const hasUserIntentRef = useRef(false);
  const isDismissingRef = useRef(false);
  // Set directly inside the divider observer callback rather than mirrored via an effect,
  // so the unmount cleanup below can read it synchronously even when unmount and the
  // triggering event land in the same batch.
  const dividerInViewRef = useRef<boolean | null>(null);

  useEffect(() => {
    isDismissingRef.current = isDismissing;
  }, [isDismissing]);

  const messageEntries = useMemo(
    () => entries.filter((entry) => entry.type === MessageTypeId.ChatMessage),
    [entries],
  );

  /*
    A group's `createdAt` is its OLDEST message's, so counting works off the individual
    messages. Entry-level comparisons stay exact anyway: the anchor is fed back into the
    registration's partitioning, which splits any group straddling the boundary.
  */
  const messages = useMemo(
    () =>
      messageEntries.flatMap((entry) => (entry.item as ChatMessageGroup<unknown>).messages),
    [messageEntries],
  );

  const latestMessageTimestamp = useMemo(
    () =>
      messages.reduce<string | null>(
        (latest, message) =>
          !latest || Date.parse(message.createdAt) > Date.parse(latest)
            ? message.createdAt
            : latest,
        null,
      ),
    [messages],
  );

  useEffect(() => {
    latestTimestampRef.current = latestMessageTimestamp;

    /*
      A newly-arrived message grows the thread and emits its own scroll event — without
      this, that growth-scroll can land after the settle window closes and get misread
      as user intent. Pushing the deadline forward here re-covers it, as long as the
      boundary is actually armed and the user hasn't already given real intent.
    */
    if (anchor !== null && !hasUserIntentRef.current) {
      settleDeadlineRef.current = Date.now() + SETTLE_MS;
    }
  }, [latestMessageTimestamp, anchor]);

  useEffect(() => {
    const store = useNewMessagesBoundaryStore.getState();
    const restored = store.entries[conversationId] ?? null;
    store.clearEntry(conversationId);

    setIsDismissing(false);
    setHasUserIntent(false);
    setDividerInView(null);
    hiddenSnapshotRef.current = null;
    settleDeadlineRef.current = null;
    hasUserIntentRef.current = false;
    dividerInViewRef.current = null;

    if (restored?.kind === 'anchor') {
      setAnchor(restored.anchor);
    } else if (
      restored?.kind === 'snapshot' &&
      Date.now() - restored.leftAt >= BRIEF_SWITCH_MS
    ) {
      setAnchor(restored.latestMessageTimestamp ?? new Date(restored.leftAt).toISOString());
      settleDeadlineRef.current = Date.now() + SETTLE_MS;
    } else {
      setAnchor(null);
    }

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- read at cleanup time on purpose: this is the anchor as of the OLD conversation, before the reset.
      const activeAnchor = anchorRef.current;
      // A navigating click unmounts us in the render that queues the intent update, so
      // the dismiss effect never commits — re-check its criteria off refs instead.
      const isCaughtUp =
        isDismissingRef.current ||
        (hasUserIntentRef.current && dividerInViewRef.current === true);
      if (activeAnchor !== null && !isCaughtUp) {
        useNewMessagesBoundaryStore
          .getState()
          .setEntry(conversationId, { kind: 'anchor', anchor: activeAnchor });
      } else {
        useNewMessagesBoundaryStore.getState().setEntry(conversationId, {
          kind: 'snapshot',
          latestMessageTimestamp: latestTimestampRef.current,
          leftAt: Date.now(),
        });
      }

      setAnchor(null);
    };
  }, [conversationId, setAnchor, anchorRef]);

  useEffect(() => {
    if (!isVisible) {
      hiddenSnapshotRef.current = {
        anchor: latestTimestampRef.current,
        hiddenAt: Date.now(),
      };
      return;
    }

    const snapshot = hiddenSnapshotRef.current;
    hiddenSnapshotRef.current = null;

    if (!snapshot || Date.now() - snapshot.hiddenAt < BRIEF_SWITCH_MS) return;

    /*
      A boundary the user never caught up on is kept rather than re-anchored, so unseen
      messages accumulate behind one marker instead of being silently treated as read.
    */
    if (anchorRef.current !== null && !isDismissingRef.current) return;

    setAnchor(snapshot.anchor ?? new Date(snapshot.hiddenAt).toISOString());
    setIsDismissing(false);
    setHasUserIntent(false);
    setDividerInView(null);
    settleDeadlineRef.current = Date.now() + SETTLE_MS;
    hasUserIntentRef.current = false;
    dividerInViewRef.current = null;
  }, [isVisible, setAnchor, anchorRef]);

  const newEntries = useMemo(() => {
    if (!anchor) return [] as TimelineEntry<unknown>[];

    const anchorMs = Date.parse(anchor);
    return messageEntries.filter((entry) => Date.parse(entry.timestamp) > anchorMs);
  }, [anchor, messageEntries]);

  const count = useMemo(() => {
    if (!anchor) return 0;

    const anchorMs = Date.parse(anchor);
    return messages.filter(
      (message) =>
        Date.parse(message.createdAt) > anchorMs && message.senderId !== currentUserId,
    ).length;
  }, [anchor, messages, currentUserId]);

  const isActive = anchor !== null && count > 0;

  // The timeline is sorted newest-first, so the oldest new entry is the last one.
  const boundaryKey = useMemo(() => {
    const oldestNewEntry = newEntries[newEntries.length - 1];
    return isActive && oldestNewEntry ? getTimelineKey(oldestNewEntry) : null;
  }, [isActive, newEntries]);

  useEffect(() => {
    const root = chatRef.current;
    if (!dividerEl || !root) return;

    const observer = new IntersectionObserver(
      (observed) => {
        const observedEntry = observed[0];
        if (!observedEntry) return;

        dividerInViewRef.current = observedEntry.isIntersecting;
        setDividerInView(observedEntry.isIntersecting);

        if (!observedEntry.isIntersecting && observedEntry.rootBounds) {
          setDividerOutOfViewDirection(
            observedEntry.boundingClientRect.top < observedEntry.rootBounds.top
              ? 'up'
              : 'down',
          );
        }
      },
      // the divider must clear the glass composer, not just the scroller edge, to count as seen
      { root, rootMargin: `0px 0px ${-bottomInset}px 0px` },
    );
    observer.observe(dividerEl);

    return () => observer.disconnect();
  }, [dividerEl, chatRef, bottomInset]);

  const markUserIntent = useCallback(() => {
    if (hasUserIntentRef.current) return;

    hasUserIntentRef.current = true;
    setHasUserIntent(true);
  }, []);

  /*
    Growing the thread emits scroll events on its own, so a scroll only counts as intent
    once the boundary has settled. Wheel and touch are unambiguous and bypass the wait.
  */
  const onThreadScroll = useCallback(() => {
    const settleDeadline = settleDeadlineRef.current;
    if (settleDeadline === null || Date.now() < settleDeadline) return;

    markUserIntent();
  }, [markUserIntent]);

  useEffect(() => {
    if (!isActive || isDismissing) return;
    // dividerInView === true is a CONFIRMED observation; null (not yet observed) or
    // false must never trigger a dismiss.
    if (dividerInView !== true || !hasUserIntent) return;

    setIsDismissing(true);
  }, [isActive, isDismissing, dividerInView, hasUserIntent]);

  // Like wheel and touch, a click is unambiguous intent and skips the settle wait.
  useEffect(() => {
    if (!isActive || isDismissing) return;

    document.addEventListener('click', markUserIntent, true);
    return () => document.removeEventListener('click', markUserIntent, true);
  }, [isActive, isDismissing, markUserIntent]);

  useEffect(() => {
    if (!isDismissing) return;

    const timeout = setTimeout(() => {
      setAnchor(null);
      setIsDismissing(false);
      settleDeadlineRef.current = null;
      useNewMessagesBoundaryStore.getState().clearEntry(conversationId);
    }, DISMISS_FADE_MS);

    return () => clearTimeout(timeout);
  }, [isDismissing, setAnchor, conversationId]);

  const jumpToDivider = useCallback(() => {
    dividerEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [dividerEl]);

  const registerDivider = useCallback((el: HTMLElement | null) => {
    // A newly (un)registered element hasn't been observed yet — reset to "unknown"
    // rather than carrying over the previous element's last-known visibility.
    setDividerInView(null);
    dividerInViewRef.current = null;
    setDividerEl(el);
  }, []);

  // Non-null only once the observer has CONFIRMED the divider is out of view — stays
  // hidden while dividerInView is null (not yet observed), same visible behavior as
  // before but now explicit rather than relying on a default-true guess.
  const jumpPillDirection =
    isActive && !isDismissing && dividerInView === false ? dividerOutOfViewDirection : null;

  const value = useMemo(
    () => ({
      count,
      isActive,
      isDismissing,
      boundaryKey,
      jumpPillDirection,
      registerDivider,
      markUserIntent,
      onThreadScroll,
      jumpToDivider,
    }),
    [
      count,
      isActive,
      isDismissing,
      boundaryKey,
      jumpPillDirection,
      registerDivider,
      markUserIntent,
      onThreadScroll,
      jumpToDivider,
    ],
  );

  return (
    <NewMessagesBoundaryContext.Provider value={value}>
      {children}
    </NewMessagesBoundaryContext.Provider>
  );
};
